import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Edit2, Trash2, BookOpen, Eye, ExternalLink, FileText, Sparkles, Loader2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import ImageUploadInput from "../../components/ImageUploadInput";

const EMPTY = {
    title: "", slug: "", excerpt: "", body_md: "", cover_url: "",
    category: "", tags: [], status: "draft",
    seo_title: "", seo_description: "", seo_keywords: "",
};

export default function AdminBlog() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("all");
    const [editing, setEditing] = useState(null); // null | EMPTY | post
    const [busy, setBusy] = useState(false);
    const [aiBusy, setAiBusy] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiInput, setAiInput] = useState({ topic: "", tone: "educational", audience: "general", length: "medium" });

    const generateWithAi = async () => {
        if (!aiInput.topic.trim() || aiInput.topic.trim().length < 5) return toast.error("Tell us what to write about (≥5 chars)");
        setAiBusy(true);
        try {
            const { data } = await api.post("/admin/ai/blog-draft", aiInput);
            setEditing({ ...EMPTY, ...data, tags: (data.tags || []).join(", "), status: "draft" });
            setAiOpen(false);
            toast.success("AI draft ready — review and edit before publishing");
        } catch (e) { toast.error(e.response?.data?.detail || "AI couldn't draft this article"); }
        finally { setAiBusy(false); }
    };

    const load = async () => {
        const params = filter === "all" ? {} : { status: filter };
        const { data } = await api.get("/admin/blog", { params });
        setRows(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const startNew = () => setEditing({ ...EMPTY });
    const save = async () => {
        if (!editing.title.trim() || !editing.body_md.trim()) {
            toast.error("Title and body are required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...editing,
                tags: typeof editing.tags === "string"
                    ? editing.tags.split(",").map(t => t.trim()).filter(Boolean)
                    : editing.tags,
            };
            if (editing.id) await api.put(`/admin/blog/${editing.id}`, payload);
            else await api.post("/admin/blog", payload);
            toast.success(editing.id ? "Article updated" : "Article saved");
            setEditing(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
        finally { setBusy(false); }
    };
    const togglePublish = async (p) => {
        try {
            if (p.status === "published") {
                await api.put(`/admin/blog/${p.id}`, { ...p, status: "draft" });
                toast.success("Unpublished");
            } else {
                await api.post(`/admin/blog/${p.id}/publish`);
                toast.success("Published");
            }
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}" permanently?`)) return;
        try { await api.delete(`/admin/blog/${p.id}`); toast.success("Deleted"); await load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-5" data-testid="admin-blog-page">
            <Toaster position="top-center" />
            <div className="flex items-end justify-between gap-3 flex-wrap">
                <div>
                    <div className="label-eyebrow text-sky-700">Hahnemann Journal</div>
                    <h1 className="font-serif text-4xl text-stone-900">Blog &amp; CMS</h1>
                    <p className="text-sm text-stone-500 mt-1">Publish homeopathy notes &amp; education articles. SEO meta auto-fills if you fill in description/keywords.</p>
                </div>
                <Button onClick={startNew} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="blog-new"><Plus className="w-4 h-4 mr-1" />New article</Button>
                <Button onClick={() => setAiOpen(true)} variant="outline" className="rounded-none border-purple-300 text-purple-700 hover:bg-purple-50" data-testid="blog-ai"><Sparkles className="w-4 h-4 mr-1" />AI assist</Button>
            </div>

            <div className="flex gap-1 border-b border-stone-200">
                {["all", "draft", "published"].map(f => (
                    <button key={f} onClick={() => setFilter(f)} className={`px-4 py-2 -mb-px text-sm border-b-2 ${filter === f ? "border-sky-700 text-sky-700 font-semibold" : "border-transparent text-stone-500"}`} data-testid={`blog-filter-${f}`}>
                        {f[0].toUpperCase() + f.slice(1)} {f !== "all" && <span className="ml-1 text-[10px]">({rows.filter(r => r.status === f).length})</span>}
                    </button>
                ))}
            </div>

            {rows.length === 0 ? (
                <div className="bg-white border border-stone-200 p-12 text-center text-sm text-stone-500">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                    No articles yet.
                </div>
            ) : (
                <div className="bg-white border border-stone-200 overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="bg-stone-50 border-b border-stone-200">
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Title</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Category</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Author</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Status</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Views</th>
                            <th className="text-left p-3 text-xs uppercase tracking-widest text-stone-500">Updated</th>
                            <th className="text-right p-3 text-xs uppercase tracking-widest text-stone-500">Actions</th>
                        </tr></thead>
                        <tbody>
                            {rows.map(p => (
                                <tr key={p.id} className="border-b border-stone-100 hover:bg-sky-50/40" data-testid={`blog-row-${p.id}`}>
                                    <td className="p-3">
                                        <div className="font-medium text-stone-900">{p.title}</div>
                                        <div className="text-[11px] text-stone-500 font-mono">/{p.slug}</div>
                                    </td>
                                    <td className="p-3 text-xs text-stone-600">{p.category || "—"}</td>
                                    <td className="p-3 text-xs text-stone-600">{p.author_name}</td>
                                    <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 ${p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-600"}`}>{p.status}</span></td>
                                    <td className="p-3 text-right font-mono text-stone-600">{p.views || 0}</td>
                                    <td className="p-3 text-[11px] text-stone-500">{(p.updated_at || p.created_at || "").slice(0, 10)}</td>
                                    <td className="p-3 text-right">
                                        <div className="inline-flex gap-1">
                                            {p.status === "published" && (
                                                <Link to={`/blog/${p.slug}`} target="_blank" className="inline-flex items-center text-xs text-sky-700 hover:underline px-2 py-1" data-testid={`blog-view-${p.id}`}>
                                                    <ExternalLink className="w-3 h-3" />
                                                </Link>
                                            )}
                                            <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-stone-300" onClick={() => setEditing({ ...p, tags: (p.tags || []).join(", ") })} data-testid={`blog-edit-${p.id}`}>
                                                <Edit2 className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button size="sm" variant="outline" className={`rounded-none h-7 px-2 ${p.status === "published" ? "border-amber-300 text-amber-700" : "border-emerald-300 text-emerald-700"}`} onClick={() => togglePublish(p)} data-testid={`blog-publish-${p.id}`}>
                                                {p.status === "published" ? "Unpublish" : "Publish"}
                                            </Button>
                                            <Button size="sm" variant="outline" className="rounded-none h-7 px-2 border-red-300 text-red-700 hover:bg-red-50" onClick={() => remove(p)} data-testid={`blog-delete-${p.id}`}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Editor Dialog */}
            {editing && (
                <Dialog open onOpenChange={(v) => !v && setEditing(null)}>
                    <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl">{editing.id ? "Edit article" : "New article"}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <Field label="Title">
                                <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="rounded-none border-stone-300" data-testid="blog-field-title" />
                            </Field>
                            <div className="grid sm:grid-cols-2 gap-3">
                                <Field label="Slug (optional · auto-generated)">
                                    <Input value={editing.slug} onChange={e => setEditing({ ...editing, slug: e.target.value })} placeholder="e.g. arnica-injury-uses" className="rounded-none border-stone-300 font-mono text-xs" data-testid="blog-field-slug" />
                                </Field>
                                <Field label="Category">
                                    <Input value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} placeholder="Education / Research / News…" className="rounded-none border-stone-300" data-testid="blog-field-category" />
                                </Field>
                            </div>
                            <Field label="Excerpt (1–2 sentences for cards & SEO)">
                                <Textarea value={editing.excerpt} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} rows={2} className="rounded-none border-stone-300" data-testid="blog-field-excerpt" />
                            </Field>
                            <Field label="Cover image">
                                <ImageUploadInput
                                    value={editing.cover_url}
                                    onChange={(v) => setEditing({ ...editing, cover_url: v })}
                                    testId="blog-field-cover"
                                    multiple={false}
                                    aspect="4/5"
                                    hint="Used at the top of the article and in social-share cards. 1200×1500 looks best."
                                />
                            </Field>
                            <Field label="Body (Markdown supported)">
                                <Textarea value={editing.body_md} onChange={e => setEditing({ ...editing, body_md: e.target.value })} rows={14} className="rounded-none border-stone-300 font-mono text-xs leading-relaxed" placeholder="## A subhead

A paragraph of text with **bold** and *italic* support, and [links](https://example.com).

- bullet 1
- bullet 2

> A wise quote." data-testid="blog-field-body" />
                            </Field>
                            <Field label="Tags (comma-separated)">
                                <Input value={editing.tags} onChange={e => setEditing({ ...editing, tags: e.target.value })} className="rounded-none border-stone-300" data-testid="blog-field-tags" />
                            </Field>
                            <details className="border border-stone-200 p-3">
                                <summary className="text-xs text-stone-500 cursor-pointer font-semibold uppercase tracking-widest">SEO meta (optional)</summary>
                                <div className="space-y-3 mt-3">
                                    <Field label="SEO title">
                                        <Input value={editing.seo_title} onChange={e => setEditing({ ...editing, seo_title: e.target.value })} className="rounded-none border-stone-300" data-testid="blog-field-seo-title" />
                                    </Field>
                                    <Field label="Meta description">
                                        <Input value={editing.seo_description} onChange={e => setEditing({ ...editing, seo_description: e.target.value })} className="rounded-none border-stone-300" data-testid="blog-field-seo-desc" />
                                    </Field>
                                    <Field label="Meta keywords (comma-separated)">
                                        <Input value={editing.seo_keywords} onChange={e => setEditing({ ...editing, seo_keywords: e.target.value })} className="rounded-none border-stone-300" data-testid="blog-field-seo-keywords" />
                                    </Field>
                                </div>
                            </details>
                            <Field label="Status">
                                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                                    <SelectTrigger className="rounded-none border-stone-300 w-40"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="draft">Draft</SelectItem>
                                        <SelectItem value="published">Published</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" className="rounded-none" onClick={() => setEditing(null)} data-testid="blog-cancel">Cancel</Button>
                            <Button onClick={save} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="blog-save">{busy ? "Saving…" : "Save"}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
            {aiOpen && (
                <Dialog open onOpenChange={setAiOpen}>
                    <DialogContent className="rounded-none max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-serif text-2xl inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-700" />AI blog writer</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                            <Field label="What should the article be about?">
                                <Textarea value={aiInput.topic} onChange={e => setAiInput({ ...aiInput, topic: e.target.value })} rows={3} placeholder="e.g. How Arnica Montana helps with sports injuries — covering use, dosage, and when to consult a doctor" className="rounded-none border-stone-300" data-testid="ai-topic" />
                            </Field>
                            <div className="grid grid-cols-3 gap-3">
                                <Field label="Tone">
                                    <Select value={aiInput.tone} onValueChange={v => setAiInput({ ...aiInput, tone: v })}>
                                        <SelectTrigger className="rounded-none border-stone-300"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="educational">Educational</SelectItem>
                                            <SelectItem value="conversational">Conversational</SelectItem>
                                            <SelectItem value="clinical">Clinical</SelectItem>
                                            <SelectItem value="story">Story</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Audience">
                                    <Select value={aiInput.audience} onValueChange={v => setAiInput({ ...aiInput, audience: v })}>
                                        <SelectTrigger className="rounded-none border-stone-300"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="general">General readers</SelectItem>
                                            <SelectItem value="patients">Patients</SelectItem>
                                            <SelectItem value="doctors">Doctors</SelectItem>
                                            <SelectItem value="students">Students</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                                <Field label="Length">
                                    <Select value={aiInput.length} onValueChange={v => setAiInput({ ...aiInput, length: v })}>
                                        <SelectTrigger className="rounded-none border-stone-300"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="short">Short (~500w)</SelectItem>
                                            <SelectItem value="medium">Medium (~900w)</SelectItem>
                                            <SelectItem value="long">Long (~1300w)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </div>
                            <p className="text-[11px] text-stone-500">The AI returns a fully-structured draft with title, body, SEO meta and tags. You can edit anything before publishing.</p>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" className="rounded-none" onClick={() => setAiOpen(false)}>Cancel</Button>
                            <Button onClick={generateWithAi} disabled={aiBusy} className="rounded-none bg-purple-700 hover:bg-purple-800 text-stone-50" data-testid="ai-generate">
                                {aiBusy ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Drafting…</> : <><Sparkles className="w-4 h-4 mr-1" />Generate draft</>}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="text-[10px] uppercase tracking-widest text-stone-500">{label}</span><div className="mt-1">{children}</div></label>;
}
